
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Mail, User, AtSign, Key, Check, XCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

interface SignupStepOneProps {
  name: string;
  setName: (name: string) => void;
  email: string;
  setEmail: (email: string) => void;
  username: string;
  setUsername: (username: string) => void;
  password: string;
  setPassword: (password: string) => void;
  usernameStatus: string;
}

export const SignupStepOne = ({
  name,
  setName,
  email,
  setEmail,
  username,
  setUsername,
  password,
  setPassword,
  usernameStatus
}: SignupStepOneProps) => {
  const { t } = useTranslation();
  const [localUsername, setLocalUsername] = useState(username);
  
  // Update local state when prop changes
  useEffect(() => {
    setLocalUsername(username);
  }, [username]);
  
  // Handle username change with debounce
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setLocalUsername(value);
    
    // Only update parent state if username is at least 3 chars
    // This reduces unnecessary API calls
    if (value.length >= 3 || value.length === 0) {
      setUsername(value);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="relative">
          <Input 
            type="text" 
            placeholder={t('auth.name')} 
            value={name} 
            onChange={e => setName(e.target.value)} 
            required 
            className="pl-10 transition-all duration-200" 
          />
          <User className="absolute left-3 top-2.5 h-5 w-5 text-primary/60" />
        </div>
        
        <div className="relative">
          <Input 
            type="email" 
            placeholder={t('auth.email')} 
            value={email} 
            onChange={e => setEmail(e.target.value)} 
            required 
            className="pl-10 transition-all duration-200" 
          />
          <Mail className="absolute left-3 top-2.5 h-5 w-5 text-primary/60" />
        </div>
        
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input 
                type="text" 
                placeholder={t('auth.username')} 
                value={localUsername} 
                onChange={handleUsernameChange} 
                required 
                className={cn(
                  "pl-10 transition-all duration-200", 
                  usernameStatus === "available" && localUsername.length >= 3 && "pr-10 border-green-500/50 focus-visible:ring-green-500/20", 
                  usernameStatus === "taken" && "pr-10 border-red-500/50 focus-visible:ring-red-500/20"
                )} 
                min={3}
                maxLength={30}
              />
              <AtSign className="absolute left-3 top-2.5 h-5 w-5 text-primary/60" />
              
              {usernameStatus === "checking" && 
                <Loader2 className="absolute right-3 top-2.5 h-5 w-5 text-primary animate-spin" />
              }
              
              {usernameStatus === "available" && localUsername.length >= 3 && 
                <Check className="absolute right-3 top-2.5 h-5 w-5 text-green-500" />
              }
              
              {usernameStatus === "taken" && 
                <XCircle className="absolute right-3 top-2.5 h-5 w-5 text-red-500" />
              }
            </div>
          </div>
          {usernameStatus === "taken" && 
            <p className="text-xs text-red-500 mt-1">
              {t('auth.usernameAlreadyTaken')}
            </p>
          }
          {usernameStatus === "available" && localUsername.length >= 3 && 
            <p className="text-xs text-green-500 mt-1">
              {t('auth.usernameAvailable')}
            </p>
          }
          {localUsername.length > 0 && localUsername.length < 3 && 
            <p className="text-xs text-amber-500 mt-1">
              {t('auth.usernameLength', "Username must be at least 3 characters")}
            </p>
          }
        </div>
        
        <div className="relative">
          <Input 
            type="password" 
            placeholder={t('auth.password')} 
            value={password} 
            onChange={e => setPassword(e.target.value)} 
            required 
            className="pl-10 transition-all duration-200" 
            minLength={6}
          />
          <Key className="absolute left-3 top-2.5 h-5 w-5 text-primary/60" />
        </div>
      </div>
    </div>
  );
};
